'use client';

import Image from 'next/image';
import { Phone, Mail } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import {
  SALES_REPRESENTATIVE_QUERY,
  type SalesRepresentative,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useCurrentUser, useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton } from '@paeffgen/react/crm-documents/ui';
import { StatusTiles } from './status-tiles';
import { InvoiceTotalsTile } from './invoice-totals-tile';
import { DashboardFavorites } from './dashboard-favorites';
import { RecentActivity } from './recent-activity';
import { MaterialanfrageTile } from './materialanfrage-tile';
import { StandortAnsprechpartner } from './standort-ansprechpartner';
import { resolveRepPhotoUrl } from './team-photo';
import type { BranchEmployeesData } from './team-types';

export function Dashboard({ branchEmployees }: { branchEmployees: BranchEmployeesData }) {
  const { user, isLoading: userLoading } = useCurrentUser();
  const { adminQueryVars, allCustomers, selectedCustomers, isCrossLogin, contextHeading } = useAdmin();

  const { data: salesRepData, loading: salesRepLoading } = useQuery<{
    salesRepresentative: SalesRepresentative;
  }>(SALES_REPRESENTATIVE_QUERY, {
    variables: { ...adminQueryVars },
    fetchPolicy: 'cache-first',
  });

  const salesRep = salesRepData?.salesRepresentative;

  // Foto des Ansprechpartners aus dem statischen Filial-Team anreichern (per
  // E-Mail-Abgleich) — NAV liefert kein Bild. Kein echtes Foto → Initialen-Fallback.
  const repPhotoUrl = salesRep
    ? resolveRepPhotoUrl(branchEmployees, salesRep) ?? salesRep.photoUrl
    : null;

  return (
    <div className="space-y-10">
      {/* Header / Welcome */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          {userLoading ? (
            <>
              <Skeleton className="h-3 w-20 mb-2" />
              <Skeleton className="h-9 w-80 mb-2" />
              <Skeleton className="h-5 w-64" />
            </>
          ) : (
            <>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Guten Tag
              </p>
              <h1 className="text-3xl font-bold text-secondary tracking-tight mt-1">
                {isCrossLogin && selectedCustomers.length === 1
                  ? selectedCustomers[0].customerName
                  : user?.name ?? 'Willkommen'}
              </h1>
              <p className="text-text-muted mt-1">
                {allCustomers
                  ? selectedCustomers.length > 0
                    ? `Daten für: ${selectedCustomers.map((c) => c.customerName).join(', ')}`
                    : 'Übersicht aller Kunden'
                  : 'Willkommen zurück in Ihrem Kundenportal.'}
              </p>
            </>
          )}
        </div>

        {/* Sales Rep Quick Contact — Desktop top-right (compact) */}
        {!(allCustomers && selectedCustomers.length > 1) && !salesRepLoading && salesRep && (
          <div className="hidden md:flex items-center gap-4 bg-white px-5 py-3 rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)]">
            {repPhotoUrl ? (
              <Image
                src={repPhotoUrl}
                alt={salesRep.name}
                width={48}
                height={48}
                className="w-12 h-12 rounded-full border border-zinc-100 object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full border border-zinc-100 bg-zinc-200 flex items-center justify-center text-zinc-500 text-sm font-bold">
                {salesRep.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)}
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Ihr Ansprechpartner
              </p>
              <p className="text-sm font-bold text-secondary">
                {salesRep.name}
              </p>
            </div>
            <div className="flex gap-1 ml-4 pl-4 border-l border-zinc-100">
              <a
                href={`tel:${salesRep.phone}`}
                className="p-2 text-zinc-400 hover:text-primary hover:bg-orange-50 rounded-lg transition-colors"
                title="Anrufen"
              >
                <Phone size={16} />
              </a>
              <a
                href={`mailto:${salesRep.email}`}
                className="p-2 text-zinc-400 hover:text-primary hover:bg-orange-50 rounded-lg transition-colors"
                title="E-Mail"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Sales Rep Quick Contact — Mobile only, full-width above the KPI tiles, hidden when multi-debitor or no rep */}
      {!(allCustomers && selectedCustomers.length > 1) && !salesRepLoading && salesRep && (
        <div className="md:hidden -mt-4 flex items-center gap-4 bg-white px-4 py-3 rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)]">
          {repPhotoUrl ? (
            <Image
              src={repPhotoUrl}
              alt={salesRep.name}
              width={48}
              height={48}
              className="w-12 h-12 rounded-full border border-zinc-100 object-cover shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full border border-zinc-100 bg-zinc-200 flex items-center justify-center text-zinc-500 text-sm font-bold shrink-0">
              {salesRep.name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
              Ihr Ansprechpartner
            </p>
            <p className="text-sm font-bold text-secondary truncate">
              {salesRep.name}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <a
              href={`tel:${salesRep.phone}`}
              className="p-3 text-zinc-500 hover:text-primary hover:bg-orange-50 rounded-lg border border-zinc-200 transition-colors"
              aria-label={`Anrufen: ${salesRep.phone}`}
            >
              <Phone size={20} />
            </a>
            <a
              href={`mailto:${salesRep.email}`}
              className="p-3 text-zinc-500 hover:text-primary hover:bg-orange-50 rounded-lg border border-zinc-200 transition-colors"
              aria-label={`E-Mail an ${salesRep.email}`}
            >
              <Mail size={20} />
            </a>
          </div>
        </div>
      )}

      <StatusTiles />

      <InvoiceTotalsTile />

      {!(allCustomers && selectedCustomers.length !== 1) && <DashboardFavorites />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <RecentActivity />
        </div>

        <div className="space-y-6">
          {!(allCustomers && selectedCustomers.length !== 1) && <MaterialanfrageTile />}
        </div>
      </div>

      <StandortAnsprechpartner branchEmployees={branchEmployees} />
    </div>
  );
}
